import {
  Scene,
  ArcRotateCamera,
  Vector3,
} from '@babylonjs/core';

export class ThirdPersonCamera {
  private camera: ArcRotateCamera;
  private scene: Scene;

  private readonly sensitivity: number = 0.003;
  private readonly minDistance: number = 3;
  private readonly maxDistance: number = 15;
  private readonly defaultDistance: number = 8;

  constructor(scene: Scene, _canvas: HTMLCanvasElement) {
    this.scene = scene;

    this.camera = new ArcRotateCamera(
      'ThirdPersonCamera',
      -Math.PI / 2,  // Alpha (horizontal)
      Math.PI / 3,   // Beta (vertical)
      this.defaultDistance,
      Vector3.Zero(),
      scene
    );

    this.camera.minZ = 0.1;
    this.camera.maxZ = 500;
    this.camera.fov = 1.0;

    // Set limits
    this.camera.lowerRadiusLimit = this.minDistance;
    this.camera.upperRadiusLimit = this.maxDistance;
    this.camera.lowerBetaLimit = 0.2;
    this.camera.upperBetaLimit = Math.PI / 2 - 0.1;

    // Disable default controls
    this.camera.inputs.clear();
  }

  public rotate(deltaX: number, deltaY: number): void {
    this.camera.alpha -= deltaX * this.sensitivity;
    this.camera.beta += deltaY * this.sensitivity;

    // Clamp beta
    this.camera.beta = Math.max(
      this.camera.lowerBetaLimit!,
      Math.min(this.camera.upperBetaLimit!, this.camera.beta)
    );
  }

  public zoom(delta: number): void {
    this.camera.radius += delta * 0.5;
    this.camera.radius = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, this.camera.radius)
    );
  }

  public setTarget(position: Vector3): void {
    // Offset target to player's head height
    this.camera.setTarget(new Vector3(
      position.x,
      position.y + 1.5,
      position.z
    ));
  }

  public getForwardDirection(): Vector3 {
    // Forward direction is INTO the screen (away from camera, towards target)
    // Camera at alpha looks towards the opposite direction
    return new Vector3(
      -Math.cos(this.camera.alpha),
      0,
      -Math.sin(this.camera.alpha)
    ).normalize();
  }

  public getRightDirection(): Vector3 {
    // Right is perpendicular to forward (cross product with up)
    return new Vector3(
      -Math.sin(this.camera.alpha),
      0,
      Math.cos(this.camera.alpha)
    ).normalize();
  }

  public getLookDirection(): Vector3 {
    const direction = this.camera.getTarget().subtract(this.camera.position);
    return direction.normalize();
  }

  public getCamera(): ArcRotateCamera {
    return this.camera;
  }

  public setActive(active: boolean): void {
    if (active) {
      this.scene.activeCamera = this.camera;
    }
  }

  public getYaw(): number {
    // Return yaw angle for movement direction (into the screen)
    // yaw where sin(yaw) = -cos(alpha), cos(yaw) = -sin(alpha)
    return -this.camera.alpha - Math.PI / 2;
  }

  public setYaw(yaw: number): void {
    // Inverse of getYaw: alpha = -yaw - PI/2
    this.camera.alpha = -yaw - Math.PI / 2;
  }

  public getDistance(): number {
    return this.camera.radius;
  }

  public setDistance(distance: number): void {
    this.camera.radius = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, distance)
    );
  }

  public dispose(): void {
    this.camera.dispose();
  }
}
